import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { channelManagerText } from '../i18n/pages/channelManager';
import {
  AttentionReservation,
  bulkRemoveAttention,
  bulkResolveAttention,
  bulkSetAttentionStatus,
  getAttentionReservations,
  removeAttentionReservation,
  resolveAttentionReservation,
} from '../api.channelManager';

interface Props {
  apartmentId: number;
  /** Bumped by the parent after a link/unlink so the list refetches. */
  refreshKey?: number;
}

type Scope = 'ALL' | 'PAST' | 'INCOMING';

const STATUS_OPTIONS = ['PENDING', 'CONFIRMED', 'CHECKED_IN', 'CHECKED_OUT', 'CANCELLED'] as const;

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * A work queue, not a report.
 *
 * Reservations end up here when sync could not resolve them on its own — a source was swapped and
 * the provider does not recognise them, a stay was cancelled upstream while the guest was already
 * in the apartment, or a calendar/connection was removed and left its history behind. That last
 * case can dump dozens of rows at once (unlinking a channel-manager source with past-sync on, for
 * example), so besides the per-row actions there is one scope selector (all / past / incoming)
 * driving three bulk actions. The list is meant to empty.
 */
const AttentionReservationList: React.FC<Props> = ({ apartmentId, refreshKey }) => {
  const { lang } = useLang();
  const t = channelManagerText[lang];
  const navigate = useNavigate();

  const [rows, setRows] = useState<AttentionReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [scope, setScope] = useState<Scope>('ALL');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await getAttentionReservations(apartmentId));
    } catch {
      // A failure here must not take the surrounding panel down with it — the queue is
      // supplementary, and the rest of the channel-manager settings still work without it.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apartmentId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const isPast = (row: AttentionReservation) => row.checkOutDate < todayIso();

  const scopedRows = useMemo(() => {
    if (scope === 'PAST') return rows.filter(isPast);
    if (scope === 'INCOMING') return rows.filter((r) => !isPast(r));
    return rows;
  }, [rows, scope]);

  const pastCount = useMemo(() => rows.filter(isPast).length, [rows]);
  const incomingCount = rows.length - pastCount;

  const handleResolve = async (id: number) => {
    setResolvingId(id);
    try {
      await resolveAttentionReservation(apartmentId, id);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } finally {
      setResolvingId(null);
    }
  };

  const handleRemove = async (id: number) => {
    if (!window.confirm(t.attentionConfirmRemove)) return;
    setRemovingId(id);
    try {
      await removeAttentionReservation(apartmentId, id);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } finally {
      setRemovingId(null);
    }
  };

  const scopedIds = () => scopedRows.map((r) => r.id);

  const handleBulkResolve = async () => {
    const ids = scopedIds();
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      await bulkResolveAttention(apartmentId, ids);
      await load();
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkRemove = async () => {
    const ids = scopedIds();
    if (ids.length === 0) return;
    if (!window.confirm(t.attentionConfirmBulkRemove(ids.length))) return;
    setBulkBusy(true);
    try {
      await bulkRemoveAttention(apartmentId, ids);
      await load();
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkSetStatus = async () => {
    const ids = scopedIds();
    if (ids.length === 0 || !bulkStatus) return;
    setBulkBusy(true);
    try {
      await bulkSetAttentionStatus(apartmentId, ids, bulkStatus);
      await load();
    } finally {
      setBulkBusy(false);
    }
  };

  const statusLabel = (status: string): string => {
    switch (status) {
      case 'PENDING': return t.statusPending;
      case 'CONFIRMED': return t.statusConfirmed;
      case 'CHECKED_IN': return t.statusCheckedIn;
      case 'CHECKED_OUT': return t.statusCheckedOut;
      case 'CANCELLED': return t.statusCancelled;
      default: return status;
    }
  };

  if (loading || rows.length === 0) {
    return null;
  }

  const btnStyle: React.CSSProperties = {
    fontSize: '0.74rem',
    fontWeight: 600,
    padding: '3px 9px',
    borderRadius: 6,
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#4a5568',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const scopeBtnStyle = (active: boolean): React.CSSProperties => ({
    fontSize: '0.76rem',
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 999,
    border: '1px solid ' + (active ? '#2563eb' : '#e2e8f0'),
    background: active ? '#2563eb' : '#fff',
    color: active ? '#fff' : '#4a5568',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  });

  return (
    <div style={{ marginTop: 20 }}>
      <div
        style={{
          fontWeight: 700,
          fontSize: '0.75rem',
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: 10,
          paddingBottom: 6,
          borderBottom: '2px solid #e2e8f0',
        }}
      >
        {t.attentionTitle} ({rows.length})
      </div>

      {/* Scope + bulk actions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" style={scopeBtnStyle(scope === 'ALL')} onClick={() => setScope('ALL')}>
            {t.attentionScopeAll} ({rows.length})
          </button>
          <button type="button" style={scopeBtnStyle(scope === 'PAST')} onClick={() => setScope('PAST')}>
            {t.attentionScopePast} ({pastCount})
          </button>
          <button type="button" style={scopeBtnStyle(scope === 'INCOMING')} onClick={() => setScope('INCOMING')}>
            {t.attentionScopeIncoming} ({incomingCount})
          </button>
        </div>

        <select
          value={bulkStatus}
          onChange={(e) => setBulkStatus(e.target.value)}
          style={{
            padding: '5px 8px',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            fontSize: '0.78rem',
            background: '#fff',
            color: '#2d3748',
          }}
        >
          <option value="">{t.attentionStatusPlaceholder}</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{statusLabel(s)}</option>
          ))}
        </select>
        <button
          type="button"
          style={btnStyle}
          disabled={bulkBusy || !bulkStatus || scopedRows.length === 0}
          onClick={handleBulkSetStatus}
        >
          {t.attentionSetStatus}
        </button>
        <button
          type="button"
          style={{ ...btnStyle, color: '#166534', borderColor: '#86efac', background: '#f0fdf4' }}
          disabled={bulkBusy || scopedRows.length === 0}
          onClick={handleBulkResolve}
        >
          {t.attentionMarkResolved}
        </button>
        <button
          type="button"
          style={{ ...btnStyle, color: '#991b1b', borderColor: '#fca5a5', background: '#fef2f2' }}
          disabled={bulkBusy || scopedRows.length === 0}
          onClick={handleBulkRemove}
        >
          {t.attentionRemove}
        </button>
      </div>

      {scopedRows.map((row) => (
        <div
          key={row.id}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '8px 10px',
            borderBottom: '1px solid #f1f5f9',
            fontSize: '0.85rem',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: '#2d3748' }}>
              {row.guestName || '—'}{' '}
              <span style={{ fontWeight: 400, color: '#718096' }}>
                {row.checkInDate} → {row.checkOutDate}
              </span>
            </div>
            {row.reason && (
              <div style={{ fontSize: '0.79rem', color: '#c05621', marginTop: 2 }}>{row.reason}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              type="button"
              style={btnStyle}
              onClick={() => navigate(`/reservations/${row.id}`)}
            >
              {t.attentionEdit}
            </button>
            <button
              type="button"
              style={{ ...btnStyle, color: '#166534', borderColor: '#86efac', background: '#f0fdf4' }}
              disabled={resolvingId === row.id}
              onClick={() => handleResolve(row.id)}
            >
              {t.attentionResolve}
            </button>
            <button
              type="button"
              style={{ ...btnStyle, color: '#991b1b', borderColor: '#fca5a5', background: '#fef2f2' }}
              disabled={removingId === row.id}
              onClick={() => handleRemove(row.id)}
            >
              {t.attentionRemove}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AttentionReservationList;
